import { useState, useMemo } from "react"
import { Outlet, useLocation } from "react-router-dom"
import { Grid3X3, Moon, Sun, Eye, EyeOff, Check, X, Loader2 } from "lucide-react"
import { AppSidebar } from "./app-sidebar"
import { NotificationsDropdown } from "./notifications-dropdown"
import { GridOverlay } from "./grid-overlay"
import { generateBreadcrumbs } from "@/config"
import { useBreadcrumb } from "@/shared/context/BreadcrumbContext"
import { useTheme } from "@/shared/context/ThemeContext"
import { useAuth } from "@/auth/context/AuthContext"
import { ContentContainer } from '@/shared/components'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { Separator } from '@/shared/components/ui/separator'
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/shared/components/ui/sidebar'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { authApi } from '@/shared/services/auth'

const COUNTRY_CODES = [
  { code: "+1", label: "USA +1" },
  { code: "+52", label: "MEX +52" },
  { code: "+593", label: "ECU +593" },
  { code: "+57", label: "COL +57" },
  { code: "+51", label: "PER +51" },
  { code: "+54", label: "ARG +54" },
  { code: "+55", label: "BRA +55" },
  { code: "+56", label: "CHL +56" },
  { code: "+34", label: "ESP +34" },
]

const COMMON_PASSWORDS = [
  "password", "123456", "12345678", "qwerty", "abc123", "monkey", "1234567",
  "letmein", "trustno1", "dragon", "baseball", "iloveyou", "master", "sunshine",
  "ashley", "bailey", "shadow", "123123", "654321", "superman", "qazwsx",
  "michael", "football", "password1", "password123", "welcome", "jesus", "ninja",
  "mustang", "password2", "amanda", "jessica", "lovely", "12345", "123456789",
]

interface PasswordValidation {
  minLength: boolean
  notCommon: boolean
  notOnlyNumeric: boolean
}

function validatePassword(password: string): PasswordValidation {
  return {
    minLength: password.length >= 8,
    notCommon: !COMMON_PASSWORDS.includes(password.toLowerCase()),
    notOnlyNumeric: !/^\d+$/.test(password),
  }
}

export function AppLayout() {
  const location = useLocation()
  const { overrides } = useBreadcrumb()
  const { theme, toggleTheme } = useTheme()
  const { user, logout, checkAuth } = useAuth()
  const breadcrumbs = generateBreadcrumbs(location.pathname, overrides)
  const [showGrid, setShowGrid] = useState(false)
  const isTonalAdmin = user?.groups?.includes('admin') || user?.is_superuser

  // Modal de cambio de contraseña: solo visible justo después de iniciar sesión
  const [passwordModalDismissed, setPasswordModalDismissed] = useState(() => {
    return sessionStorage.getItem('password_modal_shown') === '1'
  })
  const showPasswordModal = !passwordModalDismissed && user && !user.password_changed_once
  const [newPassword1, setNewPassword1] = useState("")
  const [newPassword2, setNewPassword2] = useState("")
  const [passwordMismatch, setPasswordMismatch] = useState(false)
  const [showNew1, setShowNew1] = useState(false)
  const [showNew2, setShowNew2] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [successMessage, setSuccessMessage] = useState("")

  // Modal de teléfono obligatorio
  const [phoneModalDismissed, setPhoneModalDismissed] = useState(() => {
    return sessionStorage.getItem('phone_modal_shown') === '1'
  })
  const showPhoneModal = !phoneModalDismissed && user && !user.telefono && !showPasswordModal
  const [countryCode, setCountryCode] = useState("+593")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [phoneIsLoading, setPhoneIsLoading] = useState(false)
  const [phoneError, setPhoneError] = useState("")
  const [phoneSuccessMessage, setPhoneSuccessMessage] = useState("")

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, "")
    if (val.startsWith("0")) {
      val = val.substring(1)
    }
    setPhoneNumber(val)
  }

  const handlePhoneSubmit = async () => {
    setPhoneError("")
    setPhoneSuccessMessage("")

    if (!phoneNumber) {
      setPhoneError("Ingresa un número telefónico")
      return
    }

    setPhoneIsLoading(true)

    try {
      const response = await authApi.updateProfile({ telefono: countryCode + phoneNumber })

      if (response.success) {
        setPhoneSuccessMessage("Teléfono actualizado correctamente.")
        await checkAuth()
        setTimeout(() => {
          sessionStorage.setItem('phone_modal_shown', '1')
          setPhoneModalDismissed(true)
        }, 1000)
      } else {
        setPhoneError(response.message || "Error al guardar el teléfono.")
      }
    } catch {
      setPhoneError("Error de conexión. Inténtalo de nuevo.")
    } finally {
      setPhoneIsLoading(false)
    }
  }

  const passwordValidation = useMemo(() => validatePassword(newPassword1), [newPassword1])
  const isPasswordValid = newPassword1 === "" || (
    passwordValidation.minLength &&
    passwordValidation.notCommon &&
    passwordValidation.notOnlyNumeric
  )

  const handlePasswordSubmit = async () => {
    setErrors({})
    setSuccessMessage("")

    const newErrors: Record<string, string> = {}
    if (!newPassword1) newErrors.new_password1 = "Ingresa la nueva contraseña"
    if (!newPassword2) newErrors.new_password2 = "Confirma la nueva contraseña"
    if (newPassword1 && newPassword2 && newPassword1 !== newPassword2) {
      newErrors.new_password2 = "Las contraseñas no coinciden"
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsLoading(true)

    try {
      const response = await authApi.forceChangePassword({
        new_password1: newPassword1,
        new_password2: newPassword2,
      })

      if (response.success) {
        setSuccessMessage("Contraseña actualizada correctamente. Redirigiendo al inicio de sesión...")
        setTimeout(async () => {
          await logout()
        }, 1500)
      } else {
        if (response.errors) {
          setErrors(response.errors)
        } else {
          setErrors({ general: response.message })
        }
      }
    } catch {
      setErrors({ general: "Error de conexión. Inténtalo de nuevo." })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="h-screen bg-black dark:bg-[hsl(220,15%,7%)] p-1 overflow-hidden">
      <div className="h-full rounded-xl bg-white dark:bg-background shadow-lg overflow-hidden relative">
        <SidebarProvider>
          <AppSidebar />
          <SidebarInset className="bg-gray-100 dark:bg-background flex flex-col h-full">
            <header className="flex h-16 shrink-0 items-center justify-between gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 bg-gray-100 dark:bg-background px-4">
              <div className="flex items-center gap-2">
                <SidebarTrigger className="-ml-1" />
                <Separator
                  orientation="vertical"
                  className="mr-2 !h-4"
                />
                <Breadcrumb>
                  <BreadcrumbList>
                    <BreadcrumbItem className="hidden md:block">
                      <BreadcrumbLink href="/">
                        Tonal
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    {breadcrumbs.map((crumb, index) => (
                      <span key={crumb.path} className="contents">
                        <BreadcrumbSeparator className="hidden md:block" />
                        <BreadcrumbItem>
                          {index === breadcrumbs.length - 1 ? (
                            <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                          ) : (
                            <BreadcrumbLink href={crumb.path} className="hidden md:block">
                              {crumb.label}
                            </BreadcrumbLink>
                          )}
                        </BreadcrumbItem>
                      </span>
                    ))}
                    {breadcrumbs.length === 0 && (
                      <>
                        <BreadcrumbSeparator className="hidden md:block" />
                        <BreadcrumbItem>
                          <BreadcrumbPage>Dashboard</BreadcrumbPage>
                        </BreadcrumbItem>
                      </>
                    )}
                  </BreadcrumbList>
                </Breadcrumb>
              </div>
              <div className="flex items-center gap-2">
                {isTonalAdmin && (
                  <button
                    onClick={() => setShowGrid(prev => !prev)}
                    className={`rounded-md p-2 transition-colors ${
                      showGrid
                        ? 'text-blue-600 bg-blue-100 hover:bg-blue-200 dark:text-blue-400 dark:bg-blue-900/30 dark:hover:bg-blue-900/50'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700'
                    }`}
                    title={showGrid ? 'Ocultar cuadrícula' : 'Mostrar cuadrícula'}
                  >
                    <Grid3X3 className="h-5 w-5" />
                  </button>
                )}
                <button
                  onClick={toggleTheme}
                  className="rounded-md p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700 transition-colors"
                  title={theme === 'light' ? 'Modo oscuro' : 'Modo claro'}
                >
                  {theme === 'light' ? (
                    <Moon className="h-5 w-5" />
                  ) : (
                    <Sun className="h-5 w-5" />
                  )}
                </button>
                <NotificationsDropdown />
              </div>
            </header>
            <div className="flex-1 p-4 pt-0 pb-4 min-h-0">
              <ContentContainer className="relative">
                {showGrid && <GridOverlay />}
                <Outlet />
              </ContentContainer>
            </div>
          </SidebarInset>
        </SidebarProvider>
      </div>

      {/* Modal de cambio de contraseña obligatorio */}
      <Dialog open={!!showPasswordModal} onOpenChange={(open) => { if (!open) { sessionStorage.setItem('password_modal_shown', '1'); setPasswordModalDismissed(true) } }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader className="text-center">
            <img alt="Tonal" className="h-24 w-auto mx-auto mb-4" src="/logo-tonal-negro.png" />
            <DialogTitle className="text-xl">
              ¡Bienvenido a Tonal Enterprise!
            </DialogTitle>
            <DialogDescription className="text-base pt-2">
              Por motivos de seguridad es necesario que establezcas una nueva contraseña personalizada.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 pt-2">
            <div className="grid gap-2">
              <Label htmlFor="new_password1">Nueva contraseña</Label>
              <div className="relative">
                <Input
                  id="new_password1"
                  type={showNew1 ? "text" : "password"}
                  value={newPassword1}
                  onChange={(e) => {
                    const value = e.target.value
                    setNewPassword1(value)
                    setPasswordMismatch(newPassword2 !== "" && value !== "" && value !== newPassword2)
                  }}
                  placeholder="Ingresa una nueva contraseña"
                  className={errors.new_password1 || (!isPasswordValid && newPassword1) ? "border-destructive" : ""}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowNew1(!showNew1)}
                >
                  {showNew1 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {newPassword1 && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs">
                    {passwordValidation.minLength ? (
                      <Check className="h-3 w-3 text-green-600" />
                    ) : (
                      <X className="h-3 w-3 text-destructive" />
                    )}
                    <span className={passwordValidation.minLength ? "text-green-600" : "text-destructive"}>
                      Mínimo 8 caracteres
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {passwordValidation.notOnlyNumeric ? (
                      <Check className="h-3 w-3 text-green-600" />
                    ) : (
                      <X className="h-3 w-3 text-destructive" />
                    )}
                    <span className={passwordValidation.notOnlyNumeric ? "text-green-600" : "text-destructive"}>
                      No puede ser solo números
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {passwordValidation.notCommon ? (
                      <Check className="h-3 w-3 text-green-600" />
                    ) : (
                      <X className="h-3 w-3 text-destructive" />
                    )}
                    <span className={passwordValidation.notCommon ? "text-green-600" : "text-destructive"}>
                      No puede ser una contraseña común
                    </span>
                  </div>
                </div>
              )}
              {errors.new_password1 && (
                <p className="text-xs text-destructive">{errors.new_password1}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="new_password2">Confirmar nueva contraseña</Label>
              <div className="relative">
                <Input
                  id="new_password2"
                  type={showNew2 ? "text" : "password"}
                  value={newPassword2}
                  onChange={(e) => {
                    const value = e.target.value
                    setNewPassword2(value)
                    setPasswordMismatch(newPassword1 !== "" && value !== "" && newPassword1 !== value)
                  }}
                  placeholder="Confirma tu nueva contraseña"
                  className={errors.new_password2 || passwordMismatch ? "border-destructive" : ""}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowNew2(!showNew2)}
                >
                  {showNew2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {passwordMismatch && !errors.new_password2 && (
                <p className="text-xs text-destructive">Las contraseñas no coinciden</p>
              )}
              {errors.new_password2 && (
                <p className="text-xs text-destructive">{errors.new_password2}</p>
              )}
            </div>

            {errors.general && (
              <div className="rounded-md bg-destructive/10 p-3">
                <p className="text-sm text-destructive">{errors.general}</p>
              </div>
            )}

            {successMessage && (
              <div className="rounded-md bg-green-50 dark:bg-green-950/20 p-3">
                <p className="text-sm text-green-600">{successMessage}</p>
              </div>
            )}

            <Button
              onClick={handlePasswordSubmit}
              disabled={isLoading || passwordMismatch || !newPassword1 || !newPassword2 || !isPasswordValid}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Cambiar Contraseña
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de teléfono obligatorio */}
      <Dialog open={!!showPhoneModal} onOpenChange={(open) => { if (!open) { sessionStorage.setItem('phone_modal_shown', '1'); setPhoneModalDismissed(true) } }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader className="text-center">
            <img alt="Tonal" className="h-24 w-auto mx-auto mb-4" src="/logo-tonal-negro.png" />
            <DialogTitle className="text-xl">
              Actualiza tu número de teléfono
            </DialogTitle>
            <DialogDescription className="text-base pt-2">
              Necesitamos tu número de teléfono para mantenerte informado.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 pt-2">
            <div className="grid gap-2">
              <Label htmlFor="country_code">Código de país</Label>
              <Select value={countryCode} onValueChange={setCountryCode}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar país" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_CODES.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="phone_number_modal">Número telefónico</Label>
              <Input
                id="phone_number_modal"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="999999999"
              />
              <p className="text-xs text-muted-foreground">
                Solo números, sin espacios ni guiones.
              </p>
            </div>

            {countryCode && phoneNumber && (
              <div className="rounded-md bg-muted p-3">
                <p className="text-xs text-muted-foreground mb-1">Vista previa</p>
                <p className="font-medium">{countryCode}{phoneNumber}</p>
              </div>
            )}

            {phoneError && (
              <div className="rounded-md bg-destructive/10 p-3">
                <p className="text-sm text-destructive">{phoneError}</p>
              </div>
            )}

            {phoneSuccessMessage && (
              <div className="rounded-md bg-green-50 dark:bg-green-950/20 p-3">
                <p className="text-sm text-green-600">{phoneSuccessMessage}</p>
              </div>
            )}

            <Button
              onClick={handlePhoneSubmit}
              disabled={phoneIsLoading || !phoneNumber}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white"
            >
              {phoneIsLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Guardar teléfono
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
